import type {
  AysCodeCodingSystem,
  AysCodeHierarchyElementType,
} from './code-type.model';
import type { Cloneable } from '@bindoc-projects/util-domain-lib';
import { normalizeAvailableYears } from './code-years';
import {
  ICD_BLOCK_PATTERN,
  ICD_CHAPTER_PATTERN,
  ICD_SPECIFIC_PATTERN,
  OPS_BLOCK_PATTERN,
  OPS_CHAPTER_PATTERN,
  OPS_SPECIFIC_PATTERN,
} from './code-name-patterns';

export interface AysCodeResponse {
  name: string;
  hierarchyElementType: AysCodeHierarchyElementType;
  codingSystem: AysCodeCodingSystem;
  description?: string;
  year?: number;
  /**
   * The years this code actually has data for. `null` when the backend cannot
   * tell — consumers then fall back to the full InEK year range.
   */
  availableYears?: number[] | null;
}

export class AysCode implements Cloneable<AysCode> {
  public static validateCodeName(
    codeName: string,
    codingSystem: AysCodeCodingSystem,
  ): boolean {
    let result;

    switch (codingSystem) {
      case 'OPS':
        result = [
          OPS_CHAPTER_PATTERN,
          OPS_BLOCK_PATTERN,
          OPS_SPECIFIC_PATTERN,
        ].some((pattern) => pattern.test(codeName));
        break;
      case 'ICD-10-GM':
      case 'ICD-10-AT':
        result = [
          ICD_CHAPTER_PATTERN,
          ICD_BLOCK_PATTERN,
          ICD_SPECIFIC_PATTERN,
        ].some((pattern) => pattern.test(codeName));
        break;
      case 'G-DRG':
        result = !!codeName.match(
          /^(-1|0[0-9]|1[0-7]|18[AB]|19|20|21[AB]|2[2-4]|[A-Z0-9][0-9][0-9][A-Z]?|AAAA)$/,
        );
        break;
      case 'PROCEDURE-AT':
        result = !!codeName.match(
          /^(0[1-9]|1[0-9]|2[0-6]|[0-9]{2}\.[0-9]{2}|[A-Z]{2}[0-9]{3})$/,
        );
        break;
      default:
        result = true;
    }

    return result;
  }

  /**
   * The hierarchy level of a code, read from its bare name — for code lists
   * that carry names only (URL params, hand-offs). The backend picks the code
   * subtype from `hierarchyElementType` and rejects a chapter or block sent as
   * `'specific'` (BDA-6842). Leaf codes, unknown names and coding systems
   * without group formats here are `'specific'`.
   */
  public static hierarchyElementTypeOf(
    codeName: string,
    codingSystem: AysCodeCodingSystem,
  ): AysCodeHierarchyElementType {
    const name = codeName.trim();
    switch (codingSystem) {
      case 'ICD-10-GM':
      case 'ICD-10-AT': {
        const icdName = name.toUpperCase();
        // The chapter pattern also matches '' — an empty name is no chapter.
        if (icdName !== '' && ICD_CHAPTER_PATTERN.test(icdName)) {
          return 'chapter';
        }
        return ICD_BLOCK_PATTERN.test(icdName) ? 'block' : 'specific';
      }
      case 'OPS': {
        if (OPS_CHAPTER_PATTERN.test(name)) {
          return 'chapter';
        }
        return OPS_BLOCK_PATTERN.test(name) ? 'block' : 'specific';
      }
      default:
        return 'specific';
    }
  }

  public static fromObject(obj: AysCodeResponse): AysCode {
    return new AysCode(
      obj.name,
      obj.description,
      obj.codingSystem,
      obj.hierarchyElementType,
      obj.year,
      normalizeAvailableYears(obj.availableYears),
    );
  }

  constructor(
    public name: string,
    public description: string | undefined,
    public codingSystem: AysCodeCodingSystem,
    public hierarchyElementType: AysCodeHierarchyElementType,
    public year?: number,
    public availableYears?: number[],
  ) {}

  clone(): AysCode {
    return new AysCode(
      this.name,
      this.description,
      this.codingSystem,
      this.hierarchyElementType,
      this.year,
      this.availableYears ? [...this.availableYears] : undefined,
    );
  }

  public numberOfDigits(): 1 | 3 | 4 | 5 | 6 | undefined {
    switch (this.hierarchyElementType) {
      case 'chapter': {
        return 1;
      }
      case 'block':
        return undefined;
      case 'specific': {
        switch (this.codingSystem) {
          case 'G-DRG': {
            return 3;
          }
          case 'ICD-10-GM':
          case 'ICD-10-AT':
          case 'ICD-10-UK': {
            return this._cleanName(this.name).length as 3 | 4 | 5;
          }
          case 'OPS': {
            return this._cleanName(this.name).length as 3 | 4 | 5 | 6;
          }
          default:
            return undefined;
        }
      }
    }
  }

  public toJSON(): object {
    return {
      name: this.name,
      codingSystem: this.codingSystem,
      hierarchyElementType: this.hierarchyElementType,
      year: this.year,
    };
  }

  private _cleanName(name: string): string {
    const regex = new RegExp('[-+.!*#]');
    if (name.match(regex)) {
      return this._cleanName(name.replace(regex, ''));
    } else {
      return name;
    }
  }
}

export class AysCodeCount {
  public static fromObjArr(
    objArr: { code?: AysCodeResponse; count: number }[],
  ): AysCodeCount[] {
    return objArr.map(AysCodeCount.fromObj);
  }

  public static fromObj(obj: {
    code?: AysCodeResponse;
    count: number;
  }): AysCodeCount {
    const code = obj.code ? AysCode.fromObject(obj.code) : null;

    return new AysCodeCount(code, obj.count);
  }

  constructor(
    public code: AysCode,
    public count: number,
  ) {}
}
